import './home.scss';

import React, { useEffect } from 'react';
import {
  Card,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
  TabContent,
  Table,
  TabPane
} from 'reactstrap';
import { IRootState } from 'app/shared/reducers';
import { hasAnyPermission } from 'app/shared/auth/permissions';
import { connect } from 'react-redux';
import {
  getDispatcherDashboardEntries,
  getOutcomeDashboardEntries
} from 'app/entities/patient-baby/patient-baby.reducer';
import { getSettings as getOutcomeSettings } from 'app/entities/patient-outcome/patient-outcome.reducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RouteComponentProps } from 'react-router-dom';
import { dateInServerTimezone, dateInUserTimezone } from 'app/shared/util/date-utils';
import moment from 'moment';
import { JourneyStatus } from 'app/shared/model/enumerations/journey-status.model';
import { JourneyType } from 'app/shared/model/enumerations/journey-type.model';
import { getSettings as getVisitSettings } from 'app/entities/patient-visit/patient-visit.reducer';
import {
  getWatotoCareSettingsByCountryID
} from 'app/modules/administration/watotocare-settings/watotocare-settings.reducer';
import { useTranslation } from 'app/shared/util/translation-utils';


export interface IHomeProps extends StateProps, DispatchProps, RouteComponentProps {
}

export const Home = (props: IHomeProps) => {
    // Initialize the translation hook
    const { t } = useTranslation();

    const {
      userCountryId,
      outcomeDashboardEntries,
      dispatcherDashboardEntries,
      outcomeSettings,
      visitSettings,
      watotocareSettings
    } = props;
    const [outcomeDashboard, setOutcomeDashboard] = React.useState([]);
    const [dispatcherDashboard, setDispatcherDashboard] = React.useState([]);
    const [popoverOpen, setPopoverOpen] = React.useState({});
    const [sortConfig, setSortConfig] = React.useState({ key: 'motherName', direction: 'asc' });
    const TABS = {
      OUTCOMES: { tabId: '1', title: t('home.tabs.outcomes') },
      DISPATCHER: { tabId: '2', title: t('home.tabs.dispatcher') }
    };
    const [activeTab, setActiveTab] = React.useState(TABS.OUTCOMES.tabId);

    useEffect(() => {
      if (userCountryId) {
        props.getOutcomeDashboardEntries(userCountryId, dateInUserTimezone());
        props.getOutcomeSettings(userCountryId);
        props.getVisitSettings(userCountryId);
        props.getWatotoCareSettingsByCountryID(userCountryId);
      }
    }, [userCountryId]);

    const handleSort = (key) => {
      let direction = 'asc';
      if (sortConfig.key === key) {
        direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
      }
      setSortConfig({ key, direction });
    };

    const sortDashboard = (dashboard) => {
      const sortableItems = [...dashboard];
      if (sortConfig !== null) {
        sortableItems.sort((a, b) => {
          if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
            return sortConfig.direction === 'asc' ? a[sortConfig.key].localeCompare(b[sortConfig.key]) : b[sortConfig.key].localeCompare(a[sortConfig.key]);
          } else {
            if (a[sortConfig.key] < b[sortConfig.key]) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            }
          }
          return 0;
        });
      }
      return sortableItems;
    };

    const sortedOutcomeDashboard = React.useMemo(() => {
      return sortDashboard(outcomeDashboard);
    }, [outcomeDashboard, sortConfig]);

    const sortedDispatcherDashboard = React.useMemo(() => {
      return sortDashboard(dispatcherDashboard);
    }, [dispatcherDashboard, sortConfig]);

    const selectTab = (tabId) => {
      setActiveTab(tabId);
      // lazy loading the dashboard entries
      if (tabId === TABS.OUTCOMES.tabId && userCountryId) {
        props.getOutcomeDashboardEntries(userCountryId, dateInUserTimezone());
      }
      if (tabId === TABS.DISPATCHER.tabId && userCountryId) {
        props.getDispatcherDashboardEntries(userCountryId, dateInServerTimezone());
      }
    };

    useEffect(() => {
      if (outcomeDashboardEntries.length > 0 && outcomeSettings.length > 0) {

        // convert outcome settings to a map for easy access
        const outcomeSettingsByIndex = {};
        outcomeSettings.forEach((setting) => {
          outcomeSettingsByIndex[setting.index] = setting;
        });

        const tempOutcomeDashboard = [];
        outcomeDashboardEntries.forEach((entry) => {
          tempOutcomeDashboard.push({
            babyId: entry.patientBabyId,
            motherName: entry.patientName || '-',
            babyName: entry.patientBabyName || '-',
            dob: entry.patientBabyDateOfBirth,
            ageInDays: moment().startOf('day').diff(moment(entry.patientBabyDateOfBirth).startOf('day'), 'days'),
            outcomeDay: outcomeSettingsByIndex[entry.outcomesCompleted + 1]['callDay'],
            outcomeAttempts: entry.outcomeAttempts,
            motherMobile: entry.patientPhoneNumber,
            caregiverName: entry.caregiverName || '-',
            caregiverPhoneNumber: entry.caregiverPhoneNumber || '-',
            secondaryContactName: entry.secondaryContactName || '-',
            secondaryPhoneNumber: entry.secondaryPhoneNumber || '-'
          });
        });
        // sort by outcome day and then by mother name
        tempOutcomeDashboard.sort((a, b) => a.outcomeDay - b.outcomeDay || a.motherName.localeCompare(b.motherName));
        setOutcomeDashboard(tempOutcomeDashboard);
      }
    }, [outcomeDashboardEntries, outcomeSettings]);

    const getJourneyType = (type) => {
      if (type) {
        if (type === JourneyType.TELECONSULTATION) {
          return t('home.journey.types.teleconsultation');
        } else if (type === JourneyType.PHYSICAL_TRANSPORT) {
          return t('home.journey.types.physicalTransport');
        } else if (type === JourneyType.BYPASS) {
          return t('home.journey.types.journeySkipped');
        }
      }
      return '-';
    };

    const getJourneyStatus = (status, scheduledSlotStart, lastAppointmentDate) => {
      if (status) {
        if (status === JourneyStatus.COMPLETE) {
          return { journeyStatus: t('home.journey.status.needsUpdate'), journeyStatusColor: 'yellowgreen' };
        } else if (status === JourneyStatus.IN_PROGRESS) {
          return { journeyStatus: t('home.journey.status.inProgress'), journeyStatusColor: 'blue' };
        } else if (status === JourneyStatus.CANCELED) {
          return { journeyStatus: t('home.journey.status.canceled'), journeyStatusColor: 'red' };
        } else { // status === JourneyStatus.SCHEDULED
          // check if scheduledSlotStart is for today or some other day
          const scheduledSlotStartMoment = moment(scheduledSlotStart);
          const today = moment();
          if (scheduledSlotStartMoment.isSame(today, 'day')) {
            // convert scheduled slot to readable format with am pm
            const start = moment(scheduledSlotStart).format('hh:mm A');
            return { journeyStatus: t('home.journey.status.scheduledToday', { time: start }), journeyStatusColor: 'orange' };
          } else {
            // Format start time to format like July 15th, 2021
            const start = moment(scheduledSlotStart).format('MMMM Do, YYYY');
            return { journeyStatus: t('home.journey.status.scheduledFor', { date: start }), journeyStatusColor: 'orange' };
          }
        }
      }
      if (lastAppointmentDate) {
        const lastAppointmentDateString = moment(lastAppointmentDate).format('MMMM Do, YYYY');
        if (moment().isAfter(moment(lastAppointmentDate))) {
          return { journeyStatus: t('home.journey.status.appointmentOverdue'), journeyStatusColor: 'red' };
        } else {
          return { journeyStatus: t('home.journey.status.needsAppointmentBy', { date: lastAppointmentDateString }), journeyStatusColor: 'red' };
        }
      } else {
        return { journeyStatus: t('home.journey.status.needsAppointment'), journeyStatusColor: 'red' };
      }
    };

    useEffect(() => {
      if (dispatcherDashboardEntries.length > 0 && visitSettings.length > 0) {
        const tempDispatcherDashboard = [];
        dispatcherDashboardEntries.forEach((entry) => {
          const babyAgeInDays = moment().startOf('day').diff(moment(entry.patientBabyDateOfBirth).startOf('day'), 'days');

          const nextToNextVisitSettings = visitSettings.find((setting) => setting.index === (entry.visitCount + 2));
          let lastAppointmentDay: number;
          if (nextToNextVisitSettings) {
            lastAppointmentDay = nextToNextVisitSettings.visitDay - 1;
          } else {
            lastAppointmentDay = watotocareSettings.dispatcherDashboardMaxAge;
          }
          const lastAppointmentDate = lastAppointmentDay > 0 ? moment(entry.patientBabyDateOfBirth).add(lastAppointmentDay, 'days').toDate() : null;

          tempDispatcherDashboard.push({
            babyId: entry.patientBabyId,
            motherName: entry.patientName || '-',
            babyName: entry.patientBabyName || '-',
            visitCount: entry.visitCount,
            journeyType: getJourneyType(entry.journeyType),
            ...getJourneyStatus(entry.journeyStatus, entry.scheduledSlotStart, lastAppointmentDate),
            motherMobile: entry.patientPhoneNumber || '-',
            caregiverName: entry.caregiverName || '-',
            caregiverPhoneNumber: entry.caregiverPhoneNumber || '-',
            secondaryContactName: entry.secondaryContactName || '-',
            secondaryPhoneNumber: entry.secondaryPhoneNumber || '-',
            ageInDays: babyAgeInDays,
            dob: entry.patientBabyDateOfBirth
          });
        });

        // sort by visit count and then by mother name
        tempDispatcherDashboard.sort((a, b) => a.visitCount - b.visitCount || a.motherName.localeCompare(b.motherName));
        setDispatcherDashboard(tempDispatcherDashboard);
      }
    }, [dispatcherDashboardEntries, visitSettings]);

    return (
      <Container>
        <Row>
          <Col md="12">
            <Card>
              <Row>
                <Col md="12">
                  <Nav tabs>
                    <NavItem> <NavLink href="/patient/create">
                      <FontAwesomeIcon icon="plus" /> {t('home.addNewPatient')}
                    </NavLink>
                    </NavItem>
                    {Object.values(TABS).map((tab) => (
                      <NavItem key={tab.tabId}>
                        <NavLink
                          className={activeTab === tab.tabId ? 'active' : ''}
                          onClick={() => selectTab(tab.tabId)}
                        >
                          {tab.title}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                  <TabContent activeTab={activeTab} style={{ minHeight: '600px' }}>
                    <TabPane tabId={TABS.OUTCOMES.tabId}>
                      <Table responsive hover>
                        <thead>
                        <tr>
                          <th style={{ width: '24%' }} className="hand border-top-0"
                              onClick={() => handleSort('motherName')}>
                            {t('home.table.motherName')} &nbsp;
                            <FontAwesomeIcon
                              icon={sortConfig.key === 'motherName' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                          </th>
                          <th style={{ width: '18%' }} className="hand border-top-0"
                              onClick={() => handleSort('babyName')}>
                            {t('home.table.babyName')} &nbsp;
                            <FontAwesomeIcon
                              icon={sortConfig.key === 'babyName' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                          </th>
                          <th style={{ width: '18%' }} className="border-top-0">
                            {t('home.table.contactNumbers')}
                          </th>
                          <th style={{ width: '14%' }} className="hand border-top-0"
                              onClick={() => handleSort('ageInDays')}>
                            {t('home.table.babyAge')} &nbsp;
                            <FontAwesomeIcon
                              icon={sortConfig.key === 'ageInDays' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                          </th>
                          <th style={{ width: '13%' }} className="hand border-top-0"
                              onClick={() => handleSort('outcomeAttempts')}>
                            {t('home.table.outcomeAttempts')} &nbsp;
                            <FontAwesomeIcon
                              icon={sortConfig.key === 'outcomeAttempts' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                          </th>
                          <th style={{ width: '13%' }} className="hand border-top-0"
                              onClick={() => handleSort('outcomeDay')}>
                            {t('home.table.outcomeDay')} &nbsp;
                            <FontAwesomeIcon
                              icon={sortConfig.key === 'outcomeDay' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                          </th>
                        </tr>
                        </thead>
                        <tbody>
                        {outcomeDashboardEntries.length > 0 && sortedOutcomeDashboard.map((item, index) => (
                          <tr
                            key={index}
                            className="hand"
                            onClick={() => props.history.push(`/patient-outcome/create?patientBabyId=${item.babyId}`)}
                          >
                            <td>
                              {item.motherName}&nbsp;
                              <span className="superscript-icon" id={`popover-${item.babyId}`}
                                    onMouseEnter={() => setPopoverOpen({ ...popoverOpen, [item.babyId]: true })}
                                    onMouseLeave={() => setPopoverOpen({ ...popoverOpen, [item.babyId]: false })}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPopoverOpen({ ...popoverOpen, [item.babyId]: true });
                                    }}
                              >
                                  <FontAwesomeIcon icon="info-circle" />
                                </span> <Popover
                              flip
                              target={`popover-${item.babyId}`}
                              isOpen={popoverOpen[item.babyId]}
                            > <PopoverHeader>
                              {t('home.popover.additionalInfo')}
                            </PopoverHeader>
                              <PopoverBody>
                                <Row> <Col md="12">{t('home.popover.motherMobile')}: {item.motherMobile}</Col>
                                  <Col md="12">{t('common.fields.caregiverName')}: {item.caregiverName}</Col>
                                  <Col md="12">{t('common.fields.caregiverMobile')}: {item.caregiverPhoneNumber}</Col>
                                  <Col md="12">{t('common.fields.alternateName')}: {item.secondaryContactName}</Col>
                                  <Col md="12">{t('common.fields.alternateMobile')}: {item.secondaryPhoneNumber}</Col>
                                </Row>
                              </PopoverBody>
                            </Popover>
                            </td>
                            <td>{item.babyName}</td>
                            <td>
                              <Row>
                                <Col md="12">{item.motherMobile}</Col>
                                <Col md="12">{item.caregiverPhoneNumber}</Col>
                                <Col md="12">{item.secondaryPhoneNumber}</Col>
                              </Row>
                            </td>
                            <td>{item.ageInDays}</td>
                            <td>{item.outcomeAttempts}</td>
                            <td>{item.outcomeDay}</td>
                          </tr>
                        ))}
                        </tbody>
                      </Table>
                    </TabPane>
                    <TabPane tabId={TABS.DISPATCHER.tabId}> <Table responsive hover>
                      <thead>
                      <tr>
                        <th style={{ width: '24%' }} className="hand border-top-0"
                            onClick={() => handleSort('motherName')}>
                          {t('home.table.motherName')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'motherName' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                        <th style={{ width: '18%' }} className="hand border-top-0"
                            onClick={() => handleSort('babyName')}>
                          {t('home.table.babyName')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'babyName' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                        <th style={{ width: '15%' }} className="border-top-0">
                          {t('home.table.contactNumbers')}
                        </th>
                        <th style={{ width: '8%' }} className="hand border-top-0"
                            onClick={() => handleSort('ageInDays')}>
                          {t('home.table.babyAge')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'ageInDays' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                        <th style={{ width: '8%' }} className="hand border-top-0"
                            onClick={() => handleSort('visitCount')}>
                          {t('home.table.visitNumber')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'visitCount' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                        <th style={{ width: '8%' }} className="hand border-top-0"
                            onClick={() => handleSort('journeyType')}>
                          {t('common.fields.journeyType')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'journeyType' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                        <th style={{ width: '19%' }} className="hand border-top-0"
                            onClick={() => handleSort('journeyStatus')}>
                          {t('common.fields.action')} &nbsp;
                          <FontAwesomeIcon
                            icon={sortConfig.key === 'journeyStatus' ? (sortConfig.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      {dispatcherDashboardEntries.length > 0 && sortedDispatcherDashboard.map((item, index) => (
                        <tr
                          key={index}
                          className="hand"
                          onClick={() => props.history.push(`/patient-visit/journey?patientBabyId=${item.babyId}`)}
                        >
                          <td>
                            {item.motherName}&nbsp;
                            <span className="superscript-icon" id={`dispatcher-popover-${item.babyId}`}
                                  onMouseEnter={() => setPopoverOpen({ ...popoverOpen, [item.babyId]: true })}
                                  onMouseLeave={() => setPopoverOpen({ ...popoverOpen, [item.babyId]: false })}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPopoverOpen({ ...popoverOpen, [item.babyId]: true });
                                  }}
                            >
                                  <FontAwesomeIcon icon="info-circle" />
                                </span>
                            <Popover
                              flip
                              target={`dispatcher-popover-${item.babyId}`}
                              isOpen={popoverOpen[item.babyId]}
                            > <PopoverHeader>
                              {t('home.popover.additionalInfo')}
                            </PopoverHeader>
                              <PopoverBody>
                                <Row>
                                  <Col md="12">{t('home.popover.motherMobile')}: {item.motherMobile}</Col>
                                  <Col md="12">{t('common.fields.caregiverName')}: {item.caregiverName}</Col>
                                  <Col md="12">{t('common.fields.caregiverMobile')}: {item.caregiverPhoneNumber}</Col>
                                  <Col md="12">{t('common.fields.alternateName')}: {item.secondaryContactName}</Col>
                                  <Col md="12">{t('common.fields.alternateMobile')}: {item.secondaryPhoneNumber}</Col>
                                </Row>
                              </PopoverBody>
                            </Popover>
                          </td>
                          <td>{item.babyName}</td>
                          <td>
                            <Row>
                              <Col md="12">{item.motherMobile}</Col>
                              <Col md="12">{item.caregiverPhoneNumber}</Col>
                              <Col md="12">{item.secondaryPhoneNumber}</Col>
                            </Row>
                          </td>
                          <td>{item.ageInDays}</td>
                          <td>{item.visitCount}</td>
                          <td>{item.journeyType}</td>
                          <td style={{ color: item.journeyStatusColor }}>{item.journeyStatus}</td>
                        </tr>
                      ))}
                      </tbody>
                    </Table>
                    </TabPane>
                  </TabContent>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Container>);
  }
;


const mapStateToProps = ({
                           authentication,
                           patientBaby,
                           patientOutcome,
                           patientVisit,
                           watotocareSettings
                         }: IRootState) => ({
  userCountryId: authentication.userCountryId,
  isAdmin: hasAnyPermission(authentication.account.roles, ['ADMIN']),
  accountDetail: authentication.account,
  outcomeDashboardEntries: patientBaby.outcomeDashboardEntries,
  dispatcherDashboardEntries: patientBaby.dispatcherDashboardEntries,
  outcomeSettings: patientOutcome.settings,
  visitSettings: patientVisit.settings,
  watotocareSettings: watotocareSettings.settingsDetail
});

const mapDispatchToProps = {
  getOutcomeDashboardEntries,
  getDispatcherDashboardEntries,
  getOutcomeSettings,
  getVisitSettings,
  getWatotoCareSettingsByCountryID
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(Home);
